/** @module @liquidefy/code-editor/ai/ai-security @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  AISecurityConfig,
  InputValidationConfig,
  OutputFilteringConfig,
  CodeScanningConfig,
  ComplianceFramework,
  ComplianceConfig,
  DataClassification,
  AIAuditConfig,
  AIAuditEntry,
  AIRequestId,
  AIRequest,
  SecurityVulnerability,
  Range,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  now,
  DEFAULT_AI_SECURITY_CONFIG,
} from '../../code-editor.js';

// ═══ §2 INPUT VALIDATION — injection detection, sanitization ═══

/**
 * Common injection patterns.
 */
export const INJECTION_PATTERNS: readonly RegExp[] = [
  /ignore\s+(?:all\s+)?(?:previous|above|prior)\s+instructions/i,
  /disregard\s+(?:all\s+)?(?:previous|above|prior)/i,
  /you\s+are\s+now\s+(?:a|an|the)\s+/i,
  /\bsystem\s*:\s*/i,
  /\bDAN\b.*\bmode\b/i,
  /pretend\s+(?:you\s+are|to\s+be)/i,
  /act\s+as\s+(?:if\s+you\s+(?:are|were)|a|an)/i,
  /\[\s*SYSTEM\s*\]/i,
  /\{\{.*\}\}/,
  /<\|(?:im_start|system|endoftext)\|>/i,
];

/**
 * Prompt manipulation detection patterns.
 */
export const MANIPULATION_PATTERNS: readonly RegExp[] = [
  /reveal\s+(?:your|the)\s+(?:system|initial|original)\s+prompt/i,
  /what\s+(?:are|were)\s+your\s+instructions/i,
  /show\s+(?:me\s+)?your\s+(?:system\s+)?prompt/i,
  /output\s+(?:your|the)\s+(?:above|initial)\s+(?:text|prompt|instructions)/i,
  /repeat\s+(?:everything|all|the\s+text)\s+(?:above|before)/i,
  /translate\s+(?:your|the)\s+(?:system|initial)\s+(?:prompt|instructions)/i,
  /encode\s+(?:your|the)\s+(?:system|initial)\s+(?:prompt|instructions)/i,
];

/**
 * Data extraction attempt patterns.
 */
export const DATA_EXTRACTION_PATTERNS: readonly RegExp[] = [
  /(?:list|show|reveal|output|print)\s+(?:all\s+)?(?:api\s+keys|passwords|secrets|credentials|tokens)/i,
  /(?:what\s+is|tell\s+me)\s+(?:the\s+)?(?:api\s+key|password|secret|token)/i,
  /(?:dump|extract|exfiltrate)\s+(?:all\s+)?(?:data|information|content)/i,
  /send\s+(?:the\s+)?(?:data|content|information)\s+to/i,
  /(?:curl|wget|fetch|post)\s+https?:\/\//i,
];

/**
 * Validate input against all security checks.
 */
export function validateInput(input: string, config: InputValidationConfig): EditorResult<string> {
  if (isInputTooLong(input, config.maxInputLength)) {
    return err('INPUT_TOO_LONG', `Input exceeds maximum length of ${config.maxInputLength} characters`);
  }

  if (config.detectInjection) {
    const injection = detectInjection(input);
    if (injection) {
      return err('INJECTION_DETECTED', `Potential injection detected: ${injection}`);
    }
  }

  if (config.detectPromptManipulation) {
    const manipulation = detectPromptManipulation(input);
    if (manipulation) {
      return err('MANIPULATION_DETECTED', `Potential prompt manipulation detected: ${manipulation}`);
    }
  }

  if (config.detectDataExtraction) {
    const extraction = detectDataExtraction(input);
    if (extraction) {
      return err('EXTRACTION_DETECTED', `Potential data extraction attempt detected: ${extraction}`);
    }
  }

  // Check blocked patterns from config
  for (const pattern of config.blockedPatterns) {
    try {
      const regex = new RegExp(pattern, 'i');
      if (regex.test(input)) {
        return err('BLOCKED_PATTERN', `Input matches blocked pattern: ${pattern}`);
      }
    } catch {
      // Invalid regex in config, skip
    }
  }

  return ok(input, 'INPUT_VALID', 'Input passed all security checks');
}

/**
 * Detect injection attempts.
 */
export function detectInjection(input: string): string | null {
  for (const pattern of INJECTION_PATTERNS) {
    const match = input.match(pattern);
    if (match) return match[0];
  }
  return null;
}

/**
 * Detect prompt manipulation attempts.
 */
export function detectPromptManipulation(input: string): string | null {
  for (const pattern of MANIPULATION_PATTERNS) {
    const match = input.match(pattern);
    if (match) return match[0];
  }
  return null;
}

/**
 * Detect data extraction attempts.
 */
export function detectDataExtraction(input: string): string | null {
  for (const pattern of DATA_EXTRACTION_PATTERNS) {
    const match = input.match(pattern);
    if (match) return match[0];
  }
  return null;
}

/**
 * Check if input exceeds maximum length.
 */
export function isInputTooLong(input: string, maxLength: number): boolean {
  return input.length > maxLength;
}

/**
 * Sanitize input by removing potential injection markers.
 */
export function sanitizeInput(input: string): string {
  let sanitized = input;
  // Remove common prompt injection delimiters
  sanitized = sanitized.replace(/<\|[^|]*\|>/g, '');
  sanitized = sanitized.replace(/\[\s*SYSTEM\s*\]/gi, '');
  sanitized = sanitized.replace(/\{\{[^}]*\}\}/g, '');
  // Remove zero-width characters
  sanitized = sanitized.replace(/[\u200B-\u200F\u2028-\u202F\uFEFF]/g, '');
  return sanitized;
}

// ═══ §3 OUTPUT FILTERING — sensitive data, compliance ═══

/**
 * Sensitive data patterns.
 */
export const SENSITIVE_DATA_PATTERNS: readonly { readonly name: string; readonly pattern: RegExp }[] = [
  { name: 'SSN', pattern: /\b\d{3}-\d{2}-\d{4}\b/g },
  { name: 'Credit Card', pattern: /\b(?:4\d{12}(?:\d{3})?|5[1-5]\d{14}|3[47]\d{13}|6(?:011|5\d{2})\d{12})\b/g },
  { name: 'API Key', pattern: /(?:sk-|pk-|rk_|ak_)\w{20,}/g },
  { name: 'AWS Key', pattern: /AKIA[0-9A-Z]{16}/g },
  { name: 'Bearer Token', pattern: /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g },
  { name: 'Private Key', pattern: /-----BEGIN (?:RSA |EC )?PRIVATE KEY-----/g },
  { name: 'Password', pattern: /(?:password|passwd|pwd)\s*[:=]\s*['"][^'"]{4,}['"]/gi },
  { name: 'Connection String', pattern: /(?:mongodb|postgresql|mysql|redis):\/\/[^\s]+/gi },
];

/**
 * Filter output for sensitive data and compliance violations.
 */
export function filterOutput(output: string, config: OutputFilteringConfig): string {
  let filtered = output;

  if (config.filterSensitiveData) {
    filtered = filterSensitiveData(filtered);
  }

  if (config.filterCompliance) {
    filtered = filterComplianceViolations(filtered, []);
  }

  filtered = truncateOutput(filtered, config.maxOutputLength);

  // Apply custom content filters
  for (const filterPattern of config.contentFilters) {
    try {
      const regex = new RegExp(filterPattern, 'gi');
      filtered = filtered.replace(regex, '[FILTERED]');
    } catch {
      // Invalid regex, skip
    }
  }

  return filtered;
}

/**
 * Remove or mask sensitive data from output.
 */
export function filterSensitiveData(output: string): string {
  let filtered = output;
  for (const { pattern } of SENSITIVE_DATA_PATTERNS) {
    const globalPattern = new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : pattern.flags + 'g');
    filtered = filtered.replace(globalPattern, '[REDACTED]');
  }
  return filtered;
}

/**
 * Filter content that violates compliance frameworks.
 */
export function filterComplianceViolations(output: string, _frameworks: readonly ComplianceFramework[]): string {
  // Filter PII-like content
  let filtered = output;
  // Email addresses
  filtered = filtered.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]');
  // Phone numbers
  filtered = filtered.replace(/\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, '[PHONE_REDACTED]');
  return filtered;
}

/**
 * Truncate output to maximum length.
 */
export function truncateOutput(output: string, maxLength: number): string {
  if (output.length <= maxLength) return output;
  return output.slice(0, maxLength) + '\n[OUTPUT TRUNCATED]';
}

/**
 * Redact specific patterns from output.
 */
export function redactPatterns(output: string, patterns: readonly string[]): string {
  let result = output;
  for (const pattern of patterns) {
    try {
      const regex = new RegExp(pattern, 'gi');
      result = result.replace(regex, '[REDACTED]');
    } catch {
      // Invalid regex, skip
    }
  }
  return result;
}

// ═══ §4 CODE SCANNING — vulnerability detection ═══

/**
 * SQL injection detection patterns.
 */
export const SQL_INJECTION_PATTERNS: readonly RegExp[] = [
  /(?:query|execute|sql)\s*\(\s*[`'"]\s*(?:SELECT|INSERT|UPDATE|DELETE|DROP)[\s\S]*?\$\{/gi,
  /(?:query|execute|sql)\s*\(\s*(?:\w+\s*\+)/gi,
  /\.(?:raw|unsafeRaw)\s*\(/gi,
  /(?:' OR '1'='1|" OR "1"="1|'\s*OR\s*1\s*=\s*1)/gi,
];

/**
 * XSS detection patterns.
 */
export const XSS_PATTERNS: readonly RegExp[] = [
  /innerHTML\s*=/gi,
  /document\.write\s*\(/gi,
  /dangerouslySetInnerHTML/gi,
  /v-html\s*=/gi,
  /\{@html\s/gi,
  /\.html\s*\(\s*[^)]*\$\{/gi,
  /<script[\s>]/gi,
  /on(?:error|load|click|mouseover)\s*=/gi,
  /javascript:/gi,
];

/**
 * Path traversal detection patterns.
 */
export const PATH_TRAVERSAL_PATTERNS: readonly RegExp[] = [
  /\.\.\//g,
  /\.\.\\|\.\.\//g,
  /(?:readFile|writeFile|readdir|unlink)\s*\([^)]*(?:req\.|params\.|query\.|body\.)/gi,
  /path\.(?:join|resolve)\s*\([^)]*(?:req\.|params\.|query\.)/gi,
];

/**
 * Secret detection patterns.
 */
export const SECRET_PATTERNS: readonly RegExp[] = [
  /(?:password|passwd|pwd|secret|api_key|apikey|api_secret|access_token|auth_token|private_key)\s*[:=]\s*['"][^'"]{8,}['"]/gi,
  /(?:AKIA|sk-|sk_live_|rk_live_|ghp_|gho_|ghs_|ghr_|glpat-|xoxb-|xoxp-)\w{10,}/g,
  /-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----/g,
];

/**
 * Unsafe function detection patterns.
 */
export const UNSAFE_FUNCTION_PATTERNS: readonly RegExp[] = [
  /\beval\s*\(/gi,
  /new\s+Function\s*\(/gi,
  /setTimeout\s*\(\s*['"`]/gi,
  /setInterval\s*\(\s*['"`]/gi,
  /child_process/gi,
  /\bexec\s*\(\s*[^)]*(?:req|params|query|body|input|user)/gi,
];

/**
 * Scan code for security vulnerabilities.
 */
export function scanCode(code: string, config: CodeScanningConfig): SecurityVulnerability[] {
  const vulns: SecurityVulnerability[] = [];

  if (config.detectSqlInjection) vulns.push(...detectSQLInjection(code));
  if (config.detectXss) vulns.push(...detectXSS(code));
  if (config.detectPathTraversal) vulns.push(...detectPathTraversal(code));
  if (config.detectHardcodedSecrets) vulns.push(...detectHardcodedSecrets(code));
  if (config.detectUnsafeOps) vulns.push(...detectUnsafeOperations(code));

  vulns.push(...detectInsecureRandom(code));
  vulns.push(...detectWeakCrypto(code));
  vulns.push(...detectOpenRedirect(code));
  vulns.push(...detectXXE(code));

  // Apply custom rules
  for (const rule of config.customRules) {
    try {
      const regex = new RegExp(rule, 'gi');
      if (regex.test(code)) {
        vulns.push({
          type: 'custom-rule',
          severity: 'medium',
          description: `Code matches custom security rule: ${rule}`,
          mitigation: 'Review the code against the custom security rule',
        });
      }
    } catch {
      // Invalid regex, skip
    }
  }

  return vulns;
}

/**
 * Detect SQL injection vulnerabilities.
 */
export function detectSQLInjection(code: string): SecurityVulnerability[] {
  for (const pattern of SQL_INJECTION_PATTERNS) {
    if (pattern.test(code)) {
      return [{
        type: 'sql-injection',
        severity: 'critical',
        description: 'Potential SQL injection via string interpolation or concatenation',
        mitigation: 'Use parameterized queries or prepared statements',
        cweId: 'CWE-89',
      }];
    }
  }
  return [];
}

/**
 * Detect XSS vulnerabilities.
 */
export function detectXSS(code: string): SecurityVulnerability[] {
  for (const pattern of XSS_PATTERNS) {
    if (pattern.test(code)) {
      return [{
        type: 'xss',
        severity: 'high',
        description: 'Potential cross-site scripting via unsafe HTML rendering',
        mitigation: 'Sanitize user input before rendering; use text content instead of innerHTML',
        cweId: 'CWE-79',
      }];
    }
  }
  return [];
}

/**
 * Detect path traversal vulnerabilities.
 */
export function detectPathTraversal(code: string): SecurityVulnerability[] {
  for (const pattern of PATH_TRAVERSAL_PATTERNS) {
    if (pattern.test(code)) {
      return [{
        type: 'path-traversal',
        severity: 'high',
        description: 'Potential path traversal via user-controlled input',
        mitigation: 'Validate and sanitize file paths; verify resolved path is within allowed directory',
        cweId: 'CWE-22',
      }];
    }
  }
  return [];
}

/**
 * Detect hardcoded secrets.
 */
export function detectHardcodedSecrets(code: string): SecurityVulnerability[] {
  for (const pattern of SECRET_PATTERNS) {
    if (pattern.test(code)) {
      return [{
        type: 'hardcoded-secret',
        severity: 'critical',
        description: 'Hardcoded secret, API key, or private key detected',
        mitigation: 'Use environment variables or a secrets manager',
        cweId: 'CWE-798',
      }];
    }
  }
  return [];
}

/**
 * Detect unsafe operations (eval, exec, etc.).
 */
export function detectUnsafeOperations(code: string): SecurityVulnerability[] {
  for (const pattern of UNSAFE_FUNCTION_PATTERNS) {
    if (pattern.test(code)) {
      return [{
        type: 'unsafe-operation',
        severity: 'high',
        description: 'Use of unsafe function (eval, exec, dynamic code execution)',
        mitigation: 'Avoid eval and dynamic code execution; use safe alternatives',
        cweId: 'CWE-94',
      }];
    }
  }
  return [];
}

/**
 * Detect insecure random number generation.
 */
export function detectInsecureRandom(code: string): SecurityVulnerability[] {
  if (/Math\.random\s*\(\s*\)/.test(code) && /(?:token|key|secret|password|salt|nonce|iv|hash)/i.test(code)) {
    return [{
      type: 'insecure-random',
      severity: 'medium',
      description: 'Math.random() used in security-sensitive context',
      mitigation: 'Use crypto.getRandomValues() or crypto.randomBytes() for cryptographic purposes',
      cweId: 'CWE-330',
    }];
  }
  return [];
}

/**
 * Detect weak cryptography.
 */
export function detectWeakCrypto(code: string): SecurityVulnerability[] {
  const weakAlgos = /(?:md5|sha1|des|rc4|rc2|blowfish)/i;
  if (weakAlgos.test(code) && /(?:createHash|createCipher|createDecipher|cipher|hash|encrypt|decrypt)/i.test(code)) {
    return [{
      type: 'weak-crypto',
      severity: 'medium',
      description: 'Use of weak cryptographic algorithm (MD5, SHA1, DES, RC4)',
      mitigation: 'Use strong algorithms: SHA-256+, AES-256-GCM, ChaCha20-Poly1305',
      cweId: 'CWE-327',
    }];
  }
  return [];
}

/**
 * Detect open redirect vulnerabilities.
 */
export function detectOpenRedirect(code: string): SecurityVulnerability[] {
  if (/(?:redirect|location\.href|window\.location)\s*=\s*(?:req\.|params\.|query\.|body\.|input|user)/i.test(code)) {
    return [{
      type: 'open-redirect',
      severity: 'medium',
      description: 'Potential open redirect via user-controlled URL',
      mitigation: 'Validate redirect URLs against an allowlist of trusted domains',
      cweId: 'CWE-601',
    }];
  }
  return [];
}

/**
 * Detect XML External Entity (XXE) vulnerabilities.
 */
export function detectXXE(code: string): SecurityVulnerability[] {
  if (/(?:parseXML|DOMParser|xml2js|libxmljs|parseString)\s*\(/i.test(code)) {
    if (!/(?:noent|resolveExternals)\s*:\s*false/i.test(code) && !/disableExternalEntities/i.test(code)) {
      return [{
        type: 'xxe',
        severity: 'high',
        description: 'XML parsing without disabling external entities',
        mitigation: 'Disable external entity resolution and DTD processing in XML parsers',
        cweId: 'CWE-611',
      }];
    }
  }
  return [];
}

// ═══ §5 PII DETECTION — identify and anonymize personal data ═══

/**
 * PII detection patterns.
 */
export const PII_PATTERNS: readonly {
  readonly name: string;
  readonly pattern: RegExp;
  readonly type: string;
}[] = [
  { name: 'Email', pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, type: 'email' },
  { name: 'Phone', pattern: /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, type: 'phone' },
  { name: 'SSN', pattern: /\b\d{3}-\d{2}-\d{4}\b/g, type: 'ssn' },
  { name: 'Credit Card', pattern: /\b(?:4\d{12}(?:\d{3})?|5[1-5]\d{14}|3[47]\d{13}|6(?:011|5\d{2})\d{12})\b/g, type: 'credit-card' },
  { name: 'IP Address', pattern: /\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b/g, type: 'ip-address' },
];

interface PIIDetection {
  readonly type: string;
  readonly value: string;
  readonly start: number;
  readonly end: number;
}

/**
 * Detect all PII in text.
 */
export function detectPII(text: string): PIIDetection[] {
  const detections: PIIDetection[] = [];

  for (const { pattern, type } of PII_PATTERNS) {
    const globalPattern = new RegExp(pattern.source, 'g');
    let match: RegExpExecArray | null;
    while ((match = globalPattern.exec(text)) !== null) {
      detections.push({
        type,
        value: match[0],
        start: match.index,
        end: match.index + match[0].length,
      });
    }
  }

  // Sort by position
  detections.sort((a, b) => a.start - b.start);
  return detections;
}

/**
 * Detect email addresses.
 */
export function detectEmails(text: string): string[] {
  return (text.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g) ?? []);
}

/**
 * Detect phone numbers.
 */
export function detectPhoneNumbers(text: string): string[] {
  return (text.match(/\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g) ?? []);
}

/**
 * Detect Social Security Numbers.
 */
export function detectSSNs(text: string): string[] {
  return (text.match(/\b\d{3}-\d{2}-\d{4}\b/g) ?? []);
}

/**
 * Detect credit card numbers.
 */
export function detectCreditCards(text: string): string[] {
  return (text.match(/\b(?:4\d{12}(?:\d{3})?|5[1-5]\d{14}|3[47]\d{13}|6(?:011|5\d{2})\d{12})\b/g) ?? []);
}

/**
 * Detect IP addresses.
 */
export function detectIPAddresses(text: string): string[] {
  return (text.match(/\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b/g) ?? []);
}

/**
 * Detect potential names (basic heuristic: two or more capitalized words).
 */
export function detectNames(text: string): string[] {
  const matches = text.match(/\b[A-Z][a-z]+\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g) ?? [];
  // Filter out common non-name patterns
  const ignorePatterns = /^(?:The\s|A\s|An\s|In\s|On\s|At\s|To\s|For\s|Of\s|By\s|Is\s|It\s)/;
  return matches.filter((m) => !ignorePatterns.test(m));
}

/**
 * Anonymize text by replacing detected PII with placeholders.
 */
export function anonymizeText(text: string, detections: readonly PIIDetection[]): string {
  if (detections.length === 0) return text;

  // Process from end to start to preserve indices
  let result = text;
  const sorted = [...detections].sort((a, b) => b.start - a.start);
  for (const detection of sorted) {
    const replacement = `[${detection.type.toUpperCase()}]`;
    result = result.slice(0, detection.start) + replacement + result.slice(detection.end);
  }
  return result;
}

/**
 * Mask a value, optionally showing the last N characters.
 */
export function maskValue(value: string, showLast: number = 4): string {
  if (value.length <= showLast) return '*'.repeat(value.length);
  const masked = '*'.repeat(value.length - showLast);
  return masked + value.slice(-showLast);
}

// ═══ §6 COMPLIANCE — framework checks, data classification ═══

/**
 * Framework-specific requirements.
 */
export const FRAMEWORK_REQUIREMENTS: Record<ComplianceFramework, {
  readonly requireConsent: boolean;
  readonly requireAudit: boolean;
  readonly maxRetentionDays: number;
  readonly requireEncryption: boolean;
  readonly requireAnonymization: boolean;
}> = {
  gdpr: { requireConsent: true, requireAudit: true, maxRetentionDays: 365, requireEncryption: true, requireAnonymization: true },
  ccpa: { requireConsent: true, requireAudit: true, maxRetentionDays: 365, requireEncryption: true, requireAnonymization: false },
  sox: { requireConsent: false, requireAudit: true, maxRetentionDays: 2555, requireEncryption: true, requireAnonymization: false },
  hipaa: { requireConsent: true, requireAudit: true, maxRetentionDays: 2190, requireEncryption: true, requireAnonymization: true },
  iso27001: { requireConsent: false, requireAudit: true, maxRetentionDays: 1095, requireEncryption: true, requireAnonymization: false },
};

/**
 * Check compliance against one or more frameworks.
 */
export function checkCompliance(
  data: { readonly hasConsent: boolean; readonly isEncrypted: boolean; readonly isAnonymized: boolean; readonly retentionDays: number },
  frameworks: readonly ComplianceFramework[],
): { readonly compliant: boolean; readonly violations: string[] } {
  const violations: string[] = [];

  for (const framework of frameworks) {
    const reqs = FRAMEWORK_REQUIREMENTS[framework];
    if (!reqs) continue;

    if (reqs.requireConsent && !data.hasConsent) {
      violations.push(`${framework.toUpperCase()}: Requires explicit consent`);
    }
    if (reqs.requireEncryption && !data.isEncrypted) {
      violations.push(`${framework.toUpperCase()}: Requires data encryption`);
    }
    if (reqs.requireAnonymization && !data.isAnonymized) {
      violations.push(`${framework.toUpperCase()}: Requires data anonymization`);
    }
    if (data.retentionDays > reqs.maxRetentionDays) {
      violations.push(`${framework.toUpperCase()}: Retention exceeds ${reqs.maxRetentionDays} days`);
    }
  }

  return { compliant: violations.length === 0, violations };
}

/**
 * Check GDPR compliance.
 */
export function isGDPRCompliant(data: { readonly hasConsent: boolean; readonly isEncrypted: boolean; readonly isAnonymized: boolean; readonly retentionDays: number }): boolean {
  return checkCompliance(data, ['gdpr']).compliant;
}

/**
 * Check CCPA compliance.
 */
export function isCCPACompliant(data: { readonly hasConsent: boolean; readonly isEncrypted: boolean; readonly isAnonymized: boolean; readonly retentionDays: number }): boolean {
  return checkCompliance(data, ['ccpa']).compliant;
}

/**
 * Classify data based on its content.
 */
export function classifyData(data: string): DataClassification {
  const pii = detectPII(data);
  const hasSecrets = SECRET_PATTERNS.some((p) => p.test(data));

  if (hasSecrets) return 'restricted';
  if (pii.some((d) => d.type === 'ssn' || d.type === 'credit-card')) return 'restricted';
  if (pii.length > 0) return 'confidential';
  if (/\b(?:internal|proprietary|confidential)\b/i.test(data)) return 'internal';
  return 'public';
}

/**
 * Determine if consent is required for the given classification.
 */
export function requiresConsent(classification: DataClassification): boolean {
  return classification === 'confidential' || classification === 'restricted';
}

/**
 * Get the retention policy for a classification under a framework.
 */
export function retentionPolicy(
  classification: DataClassification,
  framework: ComplianceFramework,
): number {
  const reqs = FRAMEWORK_REQUIREMENTS[framework];
  if (!reqs) return 90;

  // More sensitive data has shorter retention
  switch (classification) {
    case 'restricted': return Math.min(reqs.maxRetentionDays, 90);
    case 'confidential': return Math.min(reqs.maxRetentionDays, 180);
    case 'internal': return Math.min(reqs.maxRetentionDays, 365);
    case 'public': return reqs.maxRetentionDays;
  }
}

// ═══ §7 AUDIT — logging, risk assessment, activity tracking ═══

/**
 * Risk assessment rules.
 */
export const RISK_ASSESSMENT_RULES: readonly {
  readonly pattern: RegExp;
  readonly riskLevel: AIAuditEntry['riskLevel'];
  readonly action: string;
}[] = [
  { pattern: /delete|remove|drop|truncate|destroy/i, riskLevel: 'high', action: 'destructive-operation' },
  { pattern: /admin|root|sudo|superuser/i, riskLevel: 'high', action: 'privileged-access' },
  { pattern: /password|secret|key|token|credential/i, riskLevel: 'medium', action: 'sensitive-access' },
  { pattern: /export|download|extract|dump/i, riskLevel: 'medium', action: 'data-export' },
  { pattern: /modify|update|change|alter/i, riskLevel: 'low', action: 'data-modification' },
  { pattern: /read|view|list|get|query/i, riskLevel: 'low', action: 'data-access' },
];

/**
 * Create an audit entry.
 */
export function createAuditEntry(
  requestId: AIRequestId,
  action: string,
  userId: string,
  riskLevel: AIAuditEntry['riskLevel'],
  outcome: AIAuditEntry['outcome'],
  details?: Record<string, unknown>,
): AIAuditEntry {
  return {
    id: generateId('audit'),
    requestId,
    action,
    timestamp: now(),
    userId,
    riskLevel,
    outcome,
    details: details ?? {},
  };
}

/**
 * Assess the risk level of a request.
 */
export function assessRequestRisk(request: AIRequest): AIAuditEntry['riskLevel'] {
  const contextStr = [
    request.type,
    request.context.fileUri ?? '',
    ...(request.context.symbols ?? []),
  ].join(' ');

  for (const rule of RISK_ASSESSMENT_RULES) {
    if (rule.pattern.test(contextStr)) return rule.riskLevel;
  }

  // Priority-based risk
  if (request.priority === 'critical') return 'medium';
  return 'low';
}

/**
 * Determine if an action should be audited based on config.
 */
export function shouldAudit(action: string, config: AIAuditConfig): boolean {
  if (!config.enabled) return false;
  if (config.logLevel === 'verbose') return true;
  if (config.logLevel === 'standard') {
    // Audit all except low-risk read operations
    return !/^(?:read|view|list|get|query)$/i.test(action);
  }
  // Minimal: only high-risk actions
  return /(?:delete|admin|export|credential|secret)/i.test(action);
}

/**
 * Format an audit entry as a human-readable string.
 */
export function formatAuditEntry(entry: AIAuditEntry): string {
  const time = new Date(entry.timestamp).toISOString();
  return `[${time}] [${entry.riskLevel.toUpperCase()}] ${entry.action} by ${entry.userId} — ${entry.outcome} (${entry.id})`;
}

/**
 * Filter audit trail by optional criteria.
 */
export function auditTrail(
  entries: readonly AIAuditEntry[],
  filters?: {
    readonly userId?: string;
    readonly riskLevel?: AIAuditEntry['riskLevel'];
    readonly outcome?: AIAuditEntry['outcome'];
    readonly startTime?: number;
    readonly endTime?: number;
  },
): AIAuditEntry[] {
  let result = [...entries];

  if (filters?.userId) {
    result = result.filter((e) => e.userId === filters.userId);
  }
  if (filters?.riskLevel) {
    result = result.filter((e) => e.riskLevel === filters.riskLevel);
  }
  if (filters?.outcome) {
    result = result.filter((e) => e.outcome === filters.outcome);
  }
  if (filters?.startTime !== undefined) {
    result = result.filter((e) => e.timestamp >= filters.startTime!);
  }
  if (filters?.endTime !== undefined) {
    result = result.filter((e) => e.timestamp <= filters.endTime!);
  }

  return result.sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Detect suspicious activity patterns: multiple high-risk actions in a short window.
 */
export function suspiciousActivity(
  entries: readonly AIAuditEntry[],
  windowMs: number,
): boolean {
  const highRiskEntries = entries.filter((e) => e.riskLevel === 'high' || e.riskLevel === 'critical');
  if (highRiskEntries.length < 3) return false;

  // Sort by timestamp
  const sorted = [...highRiskEntries].sort((a, b) => a.timestamp - b.timestamp);

  // Check sliding window
  for (let i = 0; i <= sorted.length - 3; i++) {
    const windowStart = sorted[i]!.timestamp;
    const windowEnd = windowStart + windowMs;
    let count = 0;
    for (let j = i; j < sorted.length && sorted[j]!.timestamp <= windowEnd; j++) {
      count++;
    }
    if (count >= 3) return true;
  }

  return false;
}
